import * as React from "react";

import { PresentationProduct } from "../../model";
import { PlpState_UrlDataReadyToCache } from "../../url-state";
import { usePlpDataTargetUrlState } from "../plp-data-target-url-state";
import { usePlpUrlState } from "../plp-url-state-provider";
import { useProductListRunningCache } from "./product-list-running-cache-provider";
import { useProductListState } from "./product-list-state-provider";

interface ProductListUrlStateControllerConsumerInterface {
  handleNewProductListState: (value: PlpState_UrlDataReadyToCache) => void;
  handleActionProduct: (
    product: PresentationProduct,
    productIndex: number
  ) => void;
  handleNewNextPageInProductListState: (newNextPage: number) => void;
}

const defaultValue =
  {} as unknown as ProductListUrlStateControllerConsumerInterface;

const ProductListUrlStateControllerContext = React.createContext(defaultValue);

export const ProductListUrlStateControllerProvider: React.FunctionComponent = (
  props
) => {
  const { transform: transformToCacheableUrlState } =
    usePlpDataTargetUrlState();
  const { update: updateUrlState, updateOnlyPartially } = usePlpUrlState();

  const { startPage, pageSize, selectedOptions, selectedSortingOption } =
    useProductListState();
  const { facetCache } = useProductListRunningCache();

  const handleNewProductListState = React.useCallback(
    (value: PlpState_UrlDataReadyToCache) => {
      updateUrlState(value);
    },
    [updateUrlState]
  );

  const handleActionProduct = React.useCallback(
    (product: PresentationProduct, productIndex: number) => {
      const pageThatClickedProductIsOn =
        startPage + Math.floor(productIndex / pageSize);

      const nextUrlState = transformToCacheableUrlState({
        selectedOptions,
        facetCache,
        selectedSortingOption,
        page: pageThatClickedProductIsOn,
      });

      // update the location, but don't update state
      // ios safari goes back without page reload,
      updateUrlState(nextUrlState);
    },
    [startPage, pageSize, selectedOptions, facetCache, selectedSortingOption]
  );

  const handleNewNextPageInProductListState: ProductListUrlStateControllerConsumerInterface["handleNewNextPageInProductListState"] =
    React.useCallback(
      (newNextPage: number) => {
        const newProductListPartialState: Partial<PlpState_UrlDataReadyToCache> =
          {
            page: newNextPage,
          };
        updateOnlyPartially(newProductListPartialState);
      },
      [updateOnlyPartially]
    );

  const value: ProductListUrlStateControllerConsumerInterface = React.useMemo(
    () => ({
      handleNewProductListState,
      handleActionProduct,
      handleNewNextPageInProductListState,
    }),
    [
      handleNewProductListState,
      handleActionProduct,
      handleNewNextPageInProductListState,
    ]
  );
  return (
    <ProductListUrlStateControllerContext.Provider value={value}>
      {props.children || []}
    </ProductListUrlStateControllerContext.Provider>
  );
};

export const useProductListUrlStateController = () => {
  const value = React.useContext(ProductListUrlStateControllerContext);
  if (value === defaultValue) {
    throw new Error(
      "useProductListUrlStateController must be used within a ProductListUrlStateControllerProvider"
    );
  }
  return value;
};
